import Postmate from 'postmate';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { dataLayerPush } from '../components/GTMListener';
import {
  CURRENT_BROWSER,
  EXTENSION_LANDING_PATH,
  EXTENSION_ORIGINS,
} from '../constants';
import { getIsFirefox } from '../sniff';

import { genColorLog } from '../logger';
import log from '../utils/log';
import { MSG_DISCOVER_EXTENSION } from './constants';
import { parseFirefoxAnnounce } from './firefox-announce';

const STYLE = 'color:#F60;background:#FFF6EE;';
const colorLog = genColorLog(STYLE);

export const EXTENSION_PROPS = {
  extensionIsInstalled: PropTypes.bool.isRequired, // uses cache before connected
  extensionIsConnected: PropTypes.bool.isRequired, // true iff has connected in current page session
};

const CONTEXT_KEY = 'extensionProvider';
const CONTEXT_TYPES = {
  [CONTEXT_KEY]: PropTypes.shape(EXTENSION_PROPS).isRequired,
};

const LOCAL_CONNECTED_KEY = '__extensionProviderCnct';
const LOCAL_CONNECTED_VAL = '1';

const MAX_CONNECT_TIMEOUT = 10000;

export class ExtensionProvider extends Component {
  static childContextTypes = CONTEXT_TYPES;
  static propTypes = {
    extensionApiUrls: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    ListenerClass: PropTypes.func, // type Class
    container: PropTypes.object.isRequired,
    classListArray: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    children: PropTypes.object.isRequired,
  };
  static defaultProps = {
    container: document.body,
    classListArray: ['h'],
  };

  // constructor
  constructor(props) {
    super(props);

    // Firefox's discovery mechanism is a racy, timeout-bounded broadcast
    // (see getHandshakes below), unlike Chrome/Edge's deterministic
    // connection attempt against a known origin - a stale cached "true"
    // is a weaker signal there, so start pessimistic and only flip once a
    // live match resolves this session.
    const isInstalled = getIsFirefox() ? false : this._getConnected();

    this.state = {
      isInstalled,
      isConnected: false,
      extensionOrigins: [],
    };
    this._postmateChild = null;

    /** @type {string[]} */
    this._extensionOrigins = [];

    this._mounted = true;
  }

  // provider
  getChildContext() {
    const { isInstalled, isConnected, extensionOrigins } = this.state;
    return {
      [CONTEXT_KEY]: {
        extensionIsInstalled: isInstalled,
        extensionIsConnected: isConnected,
        extensionGetHandshakes: this.getHandshakes,
        extensionOrigins: extensionOrigins,
      },
    };
  }

  // life-cycle
  componentDidMount() {
    // HACK - skip for /_iframe page
    const pathname = window.location.pathname;
    const lastPathPart = pathname.split('/').pop();

    if (lastPathPart.substring(0, 1) === '_') {
      // skip extension provider for /_iframe and /_test
      colorLog(`[extension.Provider] skipping for ${pathname}`);
      return;
    }

    this.getHandshakes()
      .then((children) => {
        colorLog(`>> [Postmate.ExtProv.mounted] got handshake`);
        if (!this._mounted) {
          return;
        }
        if (!children.length) {
          throw new Error('No children found.');
        }

        this.setState({
          isConnected: true,
          isInstalled: true,
          extensionOrigins: this._extensionOrigins,
        });

        this._setConnected(true);
      })
      .catch((err) => {
        if (this._mounted) {
          colorLog(`>> [Postmate.ExtProv.mounted] handshake ERROR`, err);

          this.setState({ isConnected: false, isInstalled: false });
          this._setConnected(false);
        }
      });

    // optional listener class
    // should have constructor that takes getHandshakes promise and a
    // destroy method
    if (this.props.ListenerClass) {
      this.listener = new this.props.ListenerClass(this.getHandshakes());
    }
  }

  componentWillUnmount() {
    this._mounted = false;
    window.clearTimeout(this.timeoutId);
    if (this._postmateChild) {
      this._postmateChild.destroy();
    }
    if (this.listener && this.listener.destroy) {
      this.listener.destroy();
    }
  }

  // ## methods

  /**
   * Get 1 or more `child` from a postmate handshake with all
   * potential install of the extension.
   * @returns {Promise<Postmate.ParentAPI[]>}
   */
  getHandshakes = () => {
    // preserve `this` via arrow function

    const { extensionApiUrls, container, classListArray } = this.props;

    if (this._handshakes) {
      return this._handshakes;
    }

    if (CURRENT_BROWSER === 'firefox') {
      this._handshakes = this._getFirefoxHandshakes({
        container,
        classListArray,
      });
      return this._handshakes;
    }

    this._handshakes = new Promise((resolve, reject) => {
      colorLog(
        `>> [Postmate.ExtProv] begin handshakes: ${JSON.stringify(extensionApiUrls)}`,
      );

      /** @type {Postmate.ParentAPI[]} */
      const children = [];

      // ### Interval checking
      const startTime = new Date().getTime();
      const interval = 500;
      const maxTimeout = MAX_CONNECT_TIMEOUT;
      const maxAttempts = maxTimeout / interval;
      let attempt = 0;

      // check every 500ms to see if we got `children` or not
      // and resolve with that if present.
      //
      // TODO: handle
      const intervalId = window.setInterval(() => {
        attempt++;

        if (children.length) {
          window.clearInterval(intervalId);
          return resolve(children);
        }

        if (attempt >= maxAttempts) {
          // postmate can just hang
          const nowTime = new Date().getTime();
          colorLog(`>> [Postmate.ExtProv.TIMEDOUT] (${nowTime - startTime}ms)`);
          window.clearInterval(intervalId);
          const err = new Error('No matching URLs');
          err.name = 'NoUrls';
          return reject(err);
        }
      }, 500);

      // ### Attempt every possible extension
      for (let uriIndex = 0; uriIndex < extensionApiUrls.length; uriIndex++) {
        const extensionApiUrl = extensionApiUrls[uriIndex];
        new Postmate({
          container,
          url: extensionApiUrl,
          classListArray,
        }).then((child) => {
          const nowTime = new Date().getTime();
          colorLog(
            `>> [Postmate.ExtProv.then] got child (${nowTime - startTime}ms) (isMounted=${this._mounted})`,
          );

          if (!this._mounted) {
            child.destroy();
            return;
          }

          // HACK: extract just the `origin` from the `apiUrl`
          const extensionOrigin = extensionApiUrl
            .split('/')
            .slice(0, 3)
            .join('/');

          this._extensionOrigins.push(extensionOrigin);
          children.push(child);

          if (children.length === extensionApiUrls.length) {
            window.clearInterval(intervalId);
            resolve(children);
          }
        });
      }
    });

    return this._handshakes;
  };

  /**
   * Firefox equivalent of the Chrome/Edge `getHandshakes` flow
   * since the extension's origin can't be known in advance (see
   * docs/superpowers/plans/2026-08-19-firefox-extension-discovery.md).
   * Polls via `postMessage` for the extension's content script to
   * anounce its real p/_api.html URL, validates that URL looks like
   * the genuine extension bridge page, then conntects Postmate to it
   * exactly as the known-origin path does.
   *
   * @param {{
   *   container: HTMLElement;
   *   classListArray: string[];
   * }} opts
   */
  _getFirefoxHandshakes = ({ container, classListArray }) => {
    return new Promise((resolve, reject) => {
      /** @type {URL[]} */
      const announcedApiUrls = [];

      // ### Clean up on any resolve or reject
      let _cleanedUp = false;

      /** @returns {boolean} alreadyCleanedUp indicates if we already cleaned up */
      const _cleanUp = () => {
        if (_cleanedUp) {
          return true;
        }
        _cleanedUp = true;
        window.clearTimeout(maxTimeoutId);
        window.removeEventListener('message', onAnnounceMessage);
        return false;
      };

      // ### Overall timeout
      const startTime = new Date().getTime();

      const maxTimeoutId = window.setTimeout(() => {
        if (_cleanUp()) {
          return;
        }
        const nowTime = new Date().getTime();
        colorLog(`>> [Postmate.ExtProv.TIMEDOUT] (${nowTime - startTime}ms)`);
        const err = new Error('No matching extension');
        err.name = 'NoUrls';
        return reject(err);
      }, MAX_CONNECT_TIMEOUT);

      /**
       * ### Logic for connecting once we have an API URL
       *
       * @param {string} apiUrl
       */
      const connect = (apiUrl) => {
        new Postmate({ container, url: apiUrl, classListArray })
          .then((child) => {
            if (_cleanUp()) {
              return;
            }
            const extensionOrigin = apiUrl.split('/').slice(0, 3).join('/');
            this._extensionOrigins.push(extensionOrigin);
            resolve([child]);
          })
          .catch((err) => {
            log.error(err);
            if (_cleanUp()) {
              return;
            }
            const e = new Error('Firefox extension handshake failed');
            e.name = 'NoUrls';
            reject(e);
          });
      };

      // ### Logic for handling announce messages
      const onAnnounceMessage = (event) => {
        if (event.source !== window) {
          return;
        }
        const parsedApiUrl = parseFirefoxAnnounce(event.data);
        if (!parsedApiUrl) {
          return;
        }

        // the extension re-announces on every discover message, including
        // the one `confirmFirefoxExtensionOrigin` sends in `/_extcomm`
        if (announcedApiUrls.some((x) => x.href === parsedApiUrl.href)) {
          return;
        }

        if (announcedApiUrls.length) {
          dataLayerPush({
            event: 'gfp_extension_announce_conflict',
            reason: 'apiUrl_id_mismatch',
          });
        }

        announcedApiUrls.push(parsedApiUrl);

        if (announcedApiUrls.length === 1) {
          connect(parsedApiUrl.toString());
        }
      };

      window.addEventListener('message', onAnnounceMessage);

      // ### Send discover extension message
      window.postMessage(
        { type: MSG_DISCOVER_EXTENSION },
        window.location.origin,
      );
    });
  };

  // ## helpers

  _getConnected() {
    return window.localStorage
      ? window.localStorage.getItem(LOCAL_CONNECTED_KEY) === LOCAL_CONNECTED_VAL
      : false;
  }

  // ### Set connected
  //
  // Cache the connected state of the extension
  //
  _setConnected(isConnected) {
    if (window.localStorage) {
      window.localStorage.setItem(
        LOCAL_CONNECTED_KEY,
        isConnected ? LOCAL_CONNECTED_VAL : '',
      );
      return true;
    }
    return false;
  }

  // render
  render() {
    return <>{this.props.children}</>;
  }
}

const connectExtension = (Comp) => {
  return class ExtensionAware extends Component {
    static contextTypes = CONTEXT_TYPES;

    render() {
      const ctxData = this.context[CONTEXT_KEY];
      return <Comp {...ctxData} {...this.props} />;
    }
  };
};

export default connectExtension;

/**
 * Get the landing page for the first extension origin we found.
 * @param {string[]} extensionOrigins
 * @returns {string}
 */
export const getExtensionLanding = (extensionOrigins) => {
  const extensionOrigin = extensionOrigins[0] || EXTENSION_ORIGINS[0];
  return extensionOrigin + EXTENSION_LANDING_PATH;
};
